import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetView,
} from '@gorhom/bottom-sheet';
import { Link } from 'expo-router';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import { useQuery } from 'urql';
import { useChatDetailContext } from '@/components/Chat/ChatDetailContext';
import ChatMessageEmojiInput from '@/components/Chat/ChatMessageEmojiInput';
import { ChatMessageEmojiInputFragment } from '@/components/Chat/ChatMessageEmojiInput.utils';
import Avatar from '@/components/Custom/Avatar';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import { graphql } from '@/gql/graphql';
import useChatActionItems from '@/hooks/useChatActionItems';
import useUserId from '@/hooks/useUserId';
import formatMessageDatetime from '@/utils/formatMessageDatetime';
import { getMessageEditorHtml, resolveMessage } from '@/utils/messageMentions';
import ChatMessageItemReaction from './ChatMessageItemReaction';

const GetChatMessageDetail = graphql(/* GraphQL */ `
  query GetChatMessageDetail($messageId: uuid!) {
    message_by_pk(id: $messageId) {
      id
      created_at
      content
      content_template
      reaction_count
      chat_id
      creator {
        id
        name
        avatar_id
        color
      }
      mentions {
        id
        fallback_label
        person_id
        person {
          id
          name
        }
      }
      media(order_by: { order: asc }) {
        id
        url
        mime_type
        og_title
        og_description
        og_image_url
        og_image_height
        og_image_width
      }
      ...ChatMessageEmojiInput
    }
  }
`, [ChatMessageEmojiInputFragment]);

const ChatMessageDetailBottomSheet = BottomSheetModal<undefined>;

const UnistylesBottomSheet = withUnistyles(
  ChatMessageDetailBottomSheet,
  (_, rt) => {
    return {
      topInset: rt.insets.top,
    };
  },
);

const quickEmojis = ['❤️', '👍', '👎', '❓', '😂'];

function ChatMessageDetail() {
  const messageId = useChatDetailContext((state) => state.selectedMessageId);
  const personId = useUserId();
  const detailSheetRef = useChatDetailContext((state) => state.detailSheetRef);

  const [{ data }] = useQuery({
    query: GetChatMessageDetail,
    variables: {
      messageId: messageId!,
      personId: personId!,
    },
    pause: !messageId,
  });

  const message = data?.message_by_pk;
  const getActionItems = useChatActionItems();
  const resolvedMessage = resolveMessage(
    message?.content,
    message?.content_template,
    message?.mentions,
  );
  const editorHtml = getMessageEditorHtml(
    message?.content,
    message?.content_template,
    message?.mentions,
  );

  const actionItems = getActionItems(
    messageId!,
    resolvedMessage.plainText,
    message?.creator?.id,
    false,
    editorHtml,
  );

  const backdrop = (props: BottomSheetBackdropProps) => (
    <BottomSheetBackdrop
      enableTouchThrough
      appearsOnIndex={0}
      disappearsOnIndex={-1}
      {...props}
    />
  );

  return (
    <UnistylesBottomSheet
      backdropComponent={backdrop}
      ref={detailSheetRef}
      index={0}
      enablePanDownToClose
      enableDynamicSizing
      uniProps={(theme, rt) => ({
        backgroundStyle: {
          backgroundColor: theme.colors.backgroundMuted,
        },
        handleIndicatorStyle: {
          backgroundColor: theme.colors.backgroundDark500,
        },
        containerStyle: {
          marginHorizontal: ['base', 'xs', 'sm', 'md', 'lg'].includes(
            rt.breakpoint || '',
          )
            ? 0
            : theme.size(36),
        },
      })}
    >
      <BottomSheetView>
        <Box style={style.container}>
          <Box style={style.header}>
            <Box style={style.info}>
              {message?.creator ? (
                <Avatar
                  color={message.creator.color}
                  name={message.creator.name ?? ''}
                  id={message.creator.avatar_id ?? ''}
                  size="lg"
                />
              ) : null}

              <Box style={style.content}>
                <Box direction="horizontal" space="md" flex>
                  <Box flex>
                    <Text flexShrink={1} numberOfLines={1} size="2xl">
                      {message?.creator?.name ?? ''}
                    </Text>
                    <Text
                      flexShrink={1}
                      numberOfLines={2}
                      size="sm"
                      variant="typographySecondary"
                    >
                      {formatMessageDatetime(message?.created_at ?? '')}
                    </Text>
                  </Box>
                  <Box style={style.tools}>
                    <Box style={style.actions}>
                      {actionItems.map((button) => (
                        <ActionButton
                          key={button.name}
                          onPress={button.handler}
                          icon={button.icon}
                          label={button.text}
                          variant={button.variant}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Text size="md" numberOfLines={3}>
                  {resolvedMessage.plainText}
                </Text>
                {message ? (
                  <ChatMessageItemReaction fragment={message}>
                    {message?.reaction_count?.length > 0 ? (
                      <Box style={style.emojiActions}>
                        <Link
                          href={{
                            pathname:
                              '/chat/[chatId]/message/[messageId]/reactions',
                            params: {
                              chatId: message.chat_id,
                              messageId: message?.id,
                            },
                          }}
                          asChild
                        >
                          <ActionButton
                            forceLabel
                            variant="secondary"
                            icon="eye-outline"
                            label="View Reactions"
                          />
                        </Link>
                      </Box>
                    ) : null}
                  </ChatMessageItemReaction>
                ) : null}
              </Box>
            </Box>
          </Box>
          <Box style={style.quickEmojis}>
            {message && quickEmojis.length
              ? quickEmojis.map((emoji) => (
                  <ChatMessageEmojiInput
                    key={emoji}
                    emoji={emoji}
                    fragment={message}
                    showCount={false}
                    size="2xl"
                    onSubmit={() => {
                      detailSheetRef?.current?.close();
                    }}
                  />
                ))
              : null}
          </Box>
        </Box>
      </BottomSheetView>
    </UnistylesBottomSheet>
  );
}

const style = StyleSheet.create((theme, rt) => ({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.space('lg'),
    gap: theme.space('md'),
    paddingBottom: rt.insets.bottom + theme.space('lg'),
  },
  info: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.space('lg'),
  },
  content: {
    flex: 1,
    flexDirection: 'column',
    gap: theme.space('md'),
  },
  tools: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: theme.space('lg'),
  },
  actions: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    gap: theme.space('md'),
  },
  emojiActions: {
    flexDirection: 'row',
    gap: theme.space('md'),
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickEmojis: {
    flexDirection: 'row',
    gap: theme.space('md'),
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.size(16),
  },
  message: {
    flexDirection: 'column',
    gap: theme.space('md'),
  },
  header: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: theme.space('lg'),
  },
}));

export default ChatMessageDetail;
